import { useState, useEffect } from"react";
import { useParams, useNavigate } from"react-router-dom";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Building2, Package, DollarSign, FileCheck, Handshake, TrendingUp, ArrowLeft, Home } from"lucide-react";
import { toast } from"sonner";
import { FloorScopeProgress } from "@/components/progress/FloorScopeProgress";

const ProjectDashboardPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const { projectId } = useParams();
 const navigate = useNavigate();
 const [loading, setLoading] = useState(true);
 const [project, setProject] = useState(null);
 const [stats, setStats] = useState({
 totalUnits: 0,
 totalM2: 0,
 soldUnits: 0,
 soldM2: 0,
 availableUnits: 0,
 availableM2: 0,
 salesRevenue: 0,
 openOffers: 0,
 activePartners: 0,
 potentialSales: 0,
 projectProgress: 0,
 residentialUnits: { total: 0, sold: 0, available: 0, totalM2: 0, soldM2: 0, availableM2: 0 },
 commercialUnits: { total: 0, sold: 0, available: 0, totalM2: 0, soldM2: 0, availableM2: 0 }
 });

 useEffect(() => {
 loadProjectDashboard();
 }, [projectId]);

 const loadProjectDashboard = async () => {
 setLoading(true);
 try {
 // Fetch all data in parallel
 const [
 projectRes,
 buildingsRes,
 unitsRes,
 reservationsRes,
 offersRes,
 partnersRes,
 progressRes,
 pricingRes
 ] = await Promise.all([
 axios.get(`/projects/${projectId}`),
 axios.get("/buildings"),
 axios.get("/units"),
 axios.get("/reservations"),
 axios.get(`/offers/${projectId}`),
 axios.get(`/subcontractors/${projectId}`),
 axios.get(`/project-progress/${projectId}`),
 axios.get(`/unit-pricing/${projectId}`)
 ]);

 setProject(projectRes.data);

 // Filter data for this project
 const projectBuildings = buildingsRes.data.filter(b => b.project_id === projectId);
 const buildingIds = projectBuildings.map(b => b.id);
 const projectUnits = unitsRes.data.filter(u => buildingIds.includes(u.building_id));
 const projectOffers = offersRes.data; // Already filtered by backend
 const projectPartners = [...new Map(partnersRes.data.filter(item => item.partner_details).map(item => [item.partner_details.id, item.partner_details])).values()];

 // Calculate units statistics
 const totalUnits = projectUnits.length;
 const totalM2 = projectUnits.reduce((sum, u) => sum + (u.net_m2 || 0), 0);
 const soldUnits = projectUnits.filter(u => u.status === 'sold');
 const soldM2 = soldUnits.reduce((sum, u) => sum + (u.net_m2 || 0), 0);
 const availableUnits = projectUnits.filter(u => u.status !== 'sold');
 const availableM2 = availableUnits.reduce((sum, u) => sum + (u.net_m2 || 0), 0);

 // Residential stats
 const residentialUnits = projectUnits.filter(u => u.unit_type === 'residential');
 const residentialSold = residentialUnits.filter(u => u.status === 'sold');
 const residentialAvailable = residentialUnits.filter(u => u.status !== 'sold');

 // Commercial stats
 const commercialUnits = projectUnits.filter(u => u.unit_type === 'commercial');
 const commercialSold = commercialUnits.filter(u => u.status === 'sold');
 const commercialAvailable = commercialUnits.filter(u => u.status !== 'sold');

 // Calculate sales revenue (from reservations)
 const projectReservations = reservationsRes.data.filter(r => {
 const unit = projectUnits.find(u => u.id === r.unit_id);
 return unit && r.status === 'active';
 });
 const salesRevenue = projectReservations.reduce((sum, r) => sum + (r.contract_value || 0), 0);

 // Count open offers
 const openOffers = projectOffers.filter(o => o.status === 'pending').length;

 // Count active partners
 const activePartners = projectPartners.filter(p => p.status === 'active').length;

 // Calculate project progress
 const completedJobs = progressRes.data.filter(j => j.status === 'completed').length;
 const totalJobs = progressRes.data.length;
 const projectProgress = totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0;

 // Calculate potential sales
 const availableUnitIds = availableUnits.map(u => u.id);
 const pricingMap = {};
 pricingRes.data.forEach(p => {
 pricingMap[p.unit_id] = p;
 });

 let potentialSales = 0;
 availableUnits.forEach(unit => {
 const pricing = pricingMap[unit.id];
 if (pricing && pricing.price_per_m2) {
 potentialSales += pricing.price_per_m2 * unit.net_m2;
 }
 });

 setStats({
 totalUnits,
 totalM2,
 soldUnits: soldUnits.length,
 soldM2,
 availableUnits: availableUnits.length,
 availableM2,
 salesRevenue,
 openOffers,
 activePartners,
 potentialSales,
 projectProgress,
 residentialUnits: {
 total: residentialUnits.length,
 sold: residentialSold.length,
 available: residentialAvailable.length,
 totalM2: residentialUnits.reduce((sum, u) => sum + (u.net_m2 || 0), 0),
 soldM2: residentialSold.reduce((sum, u) => sum + (u.net_m2 || 0), 0),
 availableM2: residentialAvailable.reduce((sum, u) => sum + (u.net_m2 || 0), 0)
 },
 commercialUnits: {
 total: commercialUnits.length,
 sold: commercialSold.length,
 available: commercialAvailable.length,
 totalM2: commercialUnits.reduce((sum, u) => sum + (u.net_m2 || 0), 0),
 soldM2: commercialSold.reduce((sum, u) => sum + (u.net_m2 || 0), 0),
 availableM2: commercialAvailable.reduce((sum, u) => sum + (u.net_m2 || 0), 0)
 }
 });

 } catch (error) {
 console.error("Error loading project dashboard:", error);
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: 'EUR',
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount);
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading project dashboard...</div>
 </div>
 </Layout>
 );
 }

 if (!project) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Project not found</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 <Button
 variant="ghost"
 size="sm"
 onClick={() => navigate('/projects')}
 >
 <ArrowLeft className="w-4 h-4 mr-2"/>
 Back to Projects
 </Button>
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 {project.name} Dashboard
 </h1>
 <p className="text-sm text-slate-500">{project.code} • {project.city}</p>
 </div>
 </div>
 </div>

 {/* Main Stats Grid */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
 {/* Total Units */}
 <Card className="">
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-blue-700 flex items-center gap-2">
 <Package className="w-4 h-4"/>
 Total Units
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-semibold tabular-nums text-blue-900">{stats.totalUnits}</div>
 <p className="text-xs text-slate-600 mt-1">{stats.totalM2.toFixed(0)} m² total area</p>
 </CardContent>
 </Card>

 {/* Sold Units */}
 <Card className="">
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
 <Home className="w-4 h-4"/>
 Sold Units
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-semibold tabular-nums text-green-900">{stats.soldUnits}</div>
 <p className="text-xs text-slate-600 mt-1">{stats.soldM2.toFixed(0)} m² • {stats.totalUnits > 0 ? ((stats.soldUnits / stats.totalUnits) * 100).toFixed(1) : 0}%</p>
 </CardContent>
 </Card>

 {/* Available Units */}
 <Card className="">
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-orange-700 flex items-center gap-2">
 <Package className="w-4 h-4"/>
 Available Units
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-semibold tabular-nums text-orange-900">{stats.availableUnits}</div>
 <p className="text-xs text-slate-600 mt-1">{stats.availableM2.toFixed(0)} m² available</p>
 </CardContent>
 </Card>

 {/* Project Progress */}
 <Card className="">
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
 <TrendingUp className="w-4 h-4"/>
 Project Progress
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-semibold tabular-nums text-purple-900">{stats.projectProgress}%</div>
 <div className="w-full bg-purple-100 rounded-full h-2 mt-2">
 <div
 className="bg-purple-600 h-2 rounded-full transition-all"
 style={{ width: `${stats.projectProgress}%` }}
 />
 </div>
 <FloorScopeProgress projectId={projectId} />
 </CardContent>
 </Card>
 </div>

 {/* Financial Stats */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {/* Sales Revenue */}
 <Card className="">
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-emerald-700 flex items-center gap-2">
 <DollarSign className="w-4 h-4"/>
 Sales Revenue
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-bold text-slate-900">{formatCurrency(stats.salesRevenue)}</div>
 <p className="text-xs text-slate-600 mt-1">From sold units</p>
 </CardContent>
 </Card>

 {/* Potential Sales */}
 <Card className="">
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-cyan-700 flex items-center gap-2">
 <TrendingUp className="w-4 h-4"/>
 Potential Sales
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-bold text-slate-900">{formatCurrency(stats.potentialSales)}</div>
 <p className="text-xs text-slate-600 mt-1">From available units</p>
 </CardContent>
 </Card>

 {/* Total Potential */}
 <Card className="">
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-indigo-700 flex items-center gap-2">
 <DollarSign className="w-4 h-4"/>
 Total Value
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-bold text-slate-900">
 {formatCurrency(stats.salesRevenue + stats.potentialSales)}
 </div>
 <p className="text-xs text-slate-600 mt-1">Sold + Potential</p>
 </CardContent>
 </Card>
 </div>

 {/* Operations Stats */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {/* Open Offers */}
 <Card>
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-[#334155] flex items-center gap-2">
 <FileCheck className="w-4 h-4"/>
 Open Offers
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{stats.openOffers}</div>
 <p className="text-xs text-slate-600 mt-1">Pending contractor offers</p>
 </CardContent>
 </Card>

 {/* Active Partners */}
 <Card>
 <CardHeader className="pb-3">
 <CardTitle className="text-sm font-medium text-[#334155] flex items-center gap-2">
 <Handshake className="w-4 h-4"/>
 Active Partners
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{stats.activePartners}</div>
 <p className="text-xs text-slate-600 mt-1">Working contractors</p>
 </CardContent>
 </Card>
 </div>

 {/* Units Breakdown */}
 <Card>
 <CardHeader>
 <CardTitle>Units Breakdown</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-6">
 {/* Residential */}
 <div>
 <div className="flex items-center justify-between mb-3">
 <h3 className="text-lg font-semibold text-purple-700">Residential Units</h3>
 <span className="text-sm text-slate-500">{stats.residentialUnits.total} units</span>
 </div>
 <div className="grid grid-cols-3 gap-4">
 <div className="bg-white/50 p-4 rounded-sm">
 <div className="text-sm text-slate-600">Total</div>
 <div className="text-2xl font-bold text-slate-900">{stats.residentialUnits.totalM2.toFixed(0)} m²</div>
 </div>
 <div className="bg-green-50/80 p-4 rounded-sm">
 <div className="text-sm text-green-600">Sold</div>
 <div className="text-2xl font-bold text-slate-900">{stats.residentialUnits.soldM2.toFixed(0)} m²</div>
 <div className="text-xs text-sm text-slate-500">{stats.residentialUnits.sold} units</div>
 </div>
 <div className="bg-orange-50/80 p-4 rounded-sm">
 <div className="text-sm text-orange-600">Available</div>
 <div className="text-2xl font-bold text-slate-900">{stats.residentialUnits.availableM2.toFixed(0)} m²</div>
 <div className="text-xs text-sm text-slate-500">{stats.residentialUnits.available} units</div>
 </div>
 </div>
 </div>

 {/* Commercial */}
 {stats.commercialUnits.total > 0 && (
 <div>
 <div className="flex items-center justify-between mb-3">
 <h3 className="text-lg font-semibold text-blue-700">Commercial Units</h3>
 <span className="text-sm text-slate-500">{stats.commercialUnits.total} units</span>
 </div>
 <div className="grid grid-cols-3 gap-4">
 <div className="bg-white/50 p-4 rounded-sm">
 <div className="text-sm text-slate-600">Total</div>
 <div className="text-2xl font-bold text-slate-900">{stats.commercialUnits.totalM2.toFixed(0)} m²</div>
 </div>
 <div className="bg-green-50/80 p-4 rounded-sm">
 <div className="text-sm text-green-600">Sold</div>
 <div className="text-2xl font-bold text-slate-900">{stats.commercialUnits.soldM2.toFixed(0)} m²</div>
 <div className="text-xs text-sm text-slate-500">{stats.commercialUnits.sold} units</div>
 </div>
 <div className="bg-orange-50/80 p-4 rounded-sm">
 <div className="text-sm text-orange-600">Available</div>
 <div className="text-2xl font-bold text-slate-900">{stats.commercialUnits.availableM2.toFixed(0)} m²</div>
 <div className="text-xs text-sm text-slate-500">{stats.commercialUnits.available} units</div>
 </div>
 </div>
 </div>
 )}
 </div>
 </CardContent>
 </Card>

 {/* Quick Actions */}
 <Card>
 <CardHeader>
 <CardTitle>Quick Actions</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 <Button
 variant="outline"
 className="h-auto py-4 flex flex-col items-center gap-2"
 onClick={() => navigate('/units')}
 >
 <Package className="w-5 h-5"/>
 <span className="text-sm">View Units</span>
 </Button>
 <Button
 variant="outline"
 className="h-auto py-4 flex flex-col items-center gap-2"
 onClick={() => navigate('/project-progress')}
 >
 <TrendingUp className="w-5 h-5"/>
 <span className="text-sm">Project Progress</span>
 </Button>
 <Button
 variant="outline"
 className="h-auto py-4 flex flex-col items-center gap-2"
 onClick={() => navigate('/potential-sales')}
 >
 <DollarSign className="w-5 h-5"/>
 <span className="text-sm">Potential Sales</span>
 </Button>
 <Button
 variant="outline"
 className="h-auto py-4 flex flex-col items-center gap-2"
 onClick={() => navigate('/offers')}
 >
 <FileCheck className="w-5 h-5"/>
 <span className="text-sm">Offers</span>
 </Button>
 </div>
 </CardContent>
 </Card>
 </div>
 </Layout>
 );
};

export default ProjectDashboardPage;
